import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { ApiBaseUrls, ApiControllers } from "../../constants/constants";
import { ServiceResultModel } from "../../models/service/service-result.model";
import { ServiceUtilities } from "../../utilities/service.utilities";
import { APISanitizerUtilities } from "../../utilities/api-sanitizer.utilities";

import { CreatePropertyChargeScheduleRequest, DeletePropertyChargeScheduleRequest, PropertyChargeScheduleModel, UpdateLateFeeConfigurationsRequest, UpdatePropertyChargeScheduleRequest } from "../../models/data/property-charge-schedule.model";
import { PropertyChargeScheduleSummaryModel } from "../../models/data/property-charge-schedule-summary.model";
import { PaginatedListModel } from "../../models/service/paginated-list.model";
import { PaginationResponseHeader } from "../../models/service/pagination-response-header.model";
import { PropertyChargeSchedulesFilter } from "../../filters/property-charge-schedule.filter";
import { PropertyChargeFilter } from "../../filters/property-charge.filter";
import { MarkPropertyChargeRequest, PropertyChargeModel, UnmarkPropertyChargeRequest, WaiveLateFeesBulkActionRequest, WaiveLateFeesRequest } from "../../models/data/property-charge.model";
import { BulkActionRequest } from "../../models/service/bulk-action-request.model";
import { BulkActionResponse } from "../../models/service/bulk-action-response.model";
import { PropertyChargeScheduleAuditLogModel } from "../../models/data/property-charge-schedule-audit-log.model";
import { PropertyChargeAuditLogModel } from "../../models/data/property-charge-audit-log.model";
import { AuditLog } from "../../models/service/audit-log.model";
import { AuditLogMapper } from "../../utilities/audit-log-mapper.utilities";
import { BalancesMessagingConstants } from "../../constants/messaging.constants";
import { ChargeBulkSummaryModel } from "../../models/data/charge-bulk-summary.model";

@Injectable({
  providedIn: 'root'
})
export class ChargesService {
  private chargesApiPath: string = `${ApiBaseUrls.CORE()}${ApiControllers.CHARGES()}`;

  constructor(private httpClient: HttpClient) { }

  public async getPropertyChargeScheduleById(
    propertyChargeScheduleId: number,
    includeProperties: boolean = true
  ): Promise<ServiceResultModel<PropertyChargeScheduleModel>> {
    try {
      const requestUrl = `${this.chargesApiPath}/schedules/${propertyChargeScheduleId}?includeProperties=${includeProperties}`;
      const response = await firstValueFrom(
        this.httpClient.get<PropertyChargeScheduleModel>(
          requestUrl,
          { observe: "response", headers: { 'Accept': 'application/json' } }
        )
      );

      const schedule = <PropertyChargeScheduleModel>response.body?.valueOf();
      return <ServiceResultModel>{ data: schedule, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async getPropertyChargeScheduleSummaryById(propertyChargeScheduleId: number): Promise<ServiceResultModel<PropertyChargeScheduleSummaryModel>> {
    try {
      const requestUrl = `${this.chargesApiPath}/schedules/${propertyChargeScheduleId}/summary`;
      const response = await firstValueFrom(
        this.httpClient.get<PropertyChargeScheduleSummaryModel>(
          requestUrl,
          { observe: "response", headers: { 'Accept': 'application/json' } }
        )
      );

      const summary = <PropertyChargeScheduleSummaryModel>response.body?.valueOf();
      return <ServiceResultModel>{ data: summary, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async createPropertyChargeSchedule(
    request: CreatePropertyChargeScheduleRequest
  ): Promise<ServiceResultModel<PropertyChargeScheduleModel>> {
    try {
      const requestUrl = `${this.chargesApiPath}/schedules`;
      const response = await firstValueFrom(
        this.httpClient.post<PropertyChargeScheduleModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<CreatePropertyChargeScheduleRequest>(request)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );

      const schedule = <PropertyChargeScheduleModel>response.body?.valueOf();
      return <ServiceResultModel>{
        data: schedule,
        success: true,
        message: BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_CREATE_SUCCESS(),
        statusCode: response.status
      };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async updatePropertyChargeSchedule(
    propertyChargeScheduleId: number,
    request: UpdatePropertyChargeScheduleRequest
  ): Promise<ServiceResultModel<PropertyChargeScheduleModel>> {
    try {
      const requestUrl = `${this.chargesApiPath}/schedules/${propertyChargeScheduleId}`;
      const response = await firstValueFrom(
        this.httpClient.patch<PropertyChargeScheduleModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<UpdatePropertyChargeScheduleRequest>(request)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );

      const schedule = <PropertyChargeScheduleModel>response.body?.valueOf();
      return <ServiceResultModel>{
        data: schedule,
        success: true,
        message: BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_UPDATE_SUCCESS(),
        statusCode: response.status
      };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async deletePropertyChargeSchedule(
    propertyChargeScheduleId: number,
    request: DeletePropertyChargeScheduleRequest
  ): Promise<ServiceResultModel<PropertyChargeScheduleModel>> {
    try {
      const requestUrl = `${this.chargesApiPath}/schedules/${propertyChargeScheduleId}`;
      const response = await firstValueFrom(
        this.httpClient.delete<PropertyChargeScheduleModel>(
          requestUrl,
          {
            body: JSON.stringify(APISanitizerUtilities.sanitizePayload<DeletePropertyChargeScheduleRequest>(request)),
            observe: "response",
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
          }
        )
      );

      const schedule = <PropertyChargeScheduleModel>response.body?.valueOf();
      return <ServiceResultModel>{
        data: schedule,
        success: true,
        message: BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_DELETE_SUCCESS(),
        statusCode: response.status
      };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async updatePropertyChargeScheduleLateFees(
    propertyChargeScheduleId: number,
    request: UpdateLateFeeConfigurationsRequest
  ): Promise<ServiceResultModel<PropertyChargeScheduleModel>> {
    try {
      const requestUrl = `${this.chargesApiPath}/schedules/${propertyChargeScheduleId}/late-fees`;
      const response = await firstValueFrom(
        this.httpClient.put<PropertyChargeScheduleModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<UpdateLateFeeConfigurationsRequest>(request)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );

      const schedule = <PropertyChargeScheduleModel>response.body?.valueOf();
      return <ServiceResultModel>{
        data: schedule,
        success: true,
        message: BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_LATE_FEES_UPDATE_SUCCESS(),
        statusCode: response.status
      };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async searchPropertyChargeSchedules(propertyChargeScheduleFilter: PropertyChargeSchedulesFilter, orderBy?: string, skip?: number, take?: number): Promise<ServiceResultModel<PaginatedListModel<PropertyChargeScheduleModel>>> {
    try {
      orderBy = (orderBy) && encodeURIComponent(orderBy);
      
      const requestUrl = this.chargesApiPath + `/schedules/filter?orderBy=${orderBy}&skip=${skip}&take=${take}`;

      const response = await firstValueFrom(
        this.httpClient.post<PropertyChargeScheduleModel[]>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<PropertyChargeSchedulesFilter>(propertyChargeScheduleFilter)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      const paginationHeader: PaginationResponseHeader = JSON.parse(response.headers.get("x-pagination")!);
      const paginatedList: PaginatedListModel<PropertyChargeScheduleModel> = {
        data: <PropertyChargeScheduleModel[]>response.body?.valueOf(),
        total: paginationHeader?.total,
        skip: paginationHeader?.skip,
        take: paginationHeader?.take,
        hasPrevious: paginationHeader?.hasPrevious,
        hasNext: paginationHeader?.hasNext
      };

      return <ServiceResultModel>{ data: paginatedList, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async searchPropertyCharges(propertyChargeFilter: PropertyChargeFilter, orderBy?: string, skip?: number, take?: number): Promise<ServiceResultModel<PaginatedListModel<PropertyChargeModel>>> {
    try {
      orderBy = (orderBy) && encodeURIComponent(orderBy);
      
      const requestUrl = this.chargesApiPath + `/filter?orderBy=${orderBy}&skip=${skip}&take=${take}`;

      const response = await firstValueFrom(
        this.httpClient.post<PropertyChargeModel[]>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<PropertyChargeFilter>(propertyChargeFilter)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        ));

      const paginationHeader: PaginationResponseHeader = JSON.parse(response.headers.get("x-pagination")!);
      const paginatedList: PaginatedListModel<PropertyChargeModel> = {
        data: <PropertyChargeModel[]>response.body?.valueOf(),
        total: paginationHeader?.total,
        skip: paginationHeader?.skip,
        take: paginationHeader?.take,
        hasPrevious: paginationHeader?.hasPrevious,
        hasNext: paginationHeader?.hasNext
      };

      return <ServiceResultModel>{ data: paginatedList, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async getChargeBulkSummary(request: BulkActionRequest<PropertyChargeFilter>): Promise<ServiceResultModel<ChargeBulkSummaryModel>> {
    try {
      const requestUrl = `${this.chargesApiPath}/bulk/summary`;
      const response = await firstValueFrom(
        this.httpClient.post<ChargeBulkSummaryModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<BulkActionRequest<PropertyChargeFilter>>(request)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );

      const summary = <ChargeBulkSummaryModel>response.body?.valueOf();
      return <ServiceResultModel>{ data: summary, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async exportChargeBulkData(request: BulkActionRequest<PropertyChargeFilter>): Promise<ServiceResultModel<Blob>> {
    try {
      const requestUrl = `${this.chargesApiPath}/bulk/export`;
      const response = await firstValueFrom(
        this.httpClient.post(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<BulkActionRequest<PropertyChargeFilter>>(request)),
          { observe: "response", responseType: 'blob', headers: { 'Content-Type': 'application/json' } }
        )
      );

      return <ServiceResultModel<Blob>>{ data: response.body, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async exportChargeScheduleInvoiceData(request: BulkActionRequest<PropertyChargeSchedulesFilter>): Promise<ServiceResultModel<Blob>> {
    try {
      const requestUrl = `${this.chargesApiPath}/schedules/bulk/export`;
      const response = await firstValueFrom(
        this.httpClient.post(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<BulkActionRequest<PropertyChargeSchedulesFilter>>(request)),
          { observe: "response", responseType: 'blob', headers: { 'Content-Type': 'application/json' } }
        )
      );

      return <ServiceResultModel<Blob>>{ data: response.body, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async downloadInvoicePdf(chargeId: number): Promise<ServiceResultModel<Blob>> {
    try {
      const requestUrl = `${this.chargesApiPath}/${chargeId}/invoice`;
      const response = await firstValueFrom(
        this.httpClient.get(
          requestUrl,
          { observe: "response", responseType: 'blob' }
        )
      );

      const contentDisposition = response.headers.get('Content-Disposition');
      return <ServiceResultModel<Blob>>{
        data: response.body,
        success: true,
        statusCode: response.status,
        message: contentDisposition
      };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async resendInvoice(chargeId: number): Promise<ServiceResultModel> {
    try {
      const requestUrl = `${this.chargesApiPath}/${chargeId}/invoice/resend`;
      const response = await firstValueFrom(
        this.httpClient.post(
          requestUrl,
          null,
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );

      return <ServiceResultModel>{ success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async getAuditLogsByPropertyChargeScheduleId(
    propertyChargeScheduleId: number,
    skip: number,
    take: number,
    includeDetails: boolean = false
  ): Promise<ServiceResultModel<PaginatedListModel<AuditLog>>> {
    try {
      let requestUrl = `${this.chargesApiPath}/schedules/${propertyChargeScheduleId}/logs?skip=${skip}&take=${take}`;
      if (includeDetails) {
        requestUrl += `&includeDetails=true`;
      }
      const response = await firstValueFrom(
        this.httpClient.get<PropertyChargeScheduleAuditLogModel[]>(
          requestUrl,
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );

      const mappedData = AuditLogMapper.mapPropertyChargeScheduleAuditLogs(<PropertyChargeScheduleAuditLogModel[]>response.body?.valueOf());

      const paginationHeader: PaginationResponseHeader = JSON.parse(response.headers.get("x-pagination")!);
      const paginatedList: PaginatedListModel<AuditLog> = {
        data: mappedData,
        total: paginationHeader?.total,
        skip: paginationHeader?.skip,
        take: paginationHeader?.take,
        hasPrevious: paginationHeader?.hasPrevious,
        hasNext: paginationHeader?.hasNext
      };

      return <ServiceResultModel>{ data: paginatedList, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async getAuditLogsByPropertyChargeId(
    chargeId: number,
    skip: number,
    take: number,
    includeDetails: boolean = false
  ): Promise<ServiceResultModel<PaginatedListModel<AuditLog>>> {
    try {
      let requestUrl = `${this.chargesApiPath}/${chargeId}/logs?skip=${skip}&take=${take}`;
      if (includeDetails) {
        requestUrl += `&includeDetails=true`;
      }
      const response = await firstValueFrom(
        this.httpClient.get<PropertyChargeAuditLogModel[]>(
          requestUrl,
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );

      const mappedData = AuditLogMapper.mapPropertyChargeAuditLogs(<PropertyChargeAuditLogModel[]>(response.body?.valueOf() ?? []));

      const paginationHeader: PaginationResponseHeader = JSON.parse(response.headers.get("x-pagination")!);
      const paginatedList: PaginatedListModel<AuditLog> = {
        data: mappedData,
        total: paginationHeader?.total,
        skip: paginationHeader?.skip,
        take: paginationHeader?.take,
        hasPrevious: paginationHeader?.hasPrevious,
        hasNext: paginationHeader?.hasNext
      };

      return <ServiceResultModel>{ data: paginatedList, success: true, statusCode: response.status };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async markPropertyCharge(
    chargeId: number,
    request: MarkPropertyChargeRequest
  ): Promise<ServiceResultModel<PropertyChargeModel>> {
    try {
      const requestUrl = `${this.chargesApiPath}/${chargeId}/mark`;
      const response = await firstValueFrom(
        this.httpClient.patch<PropertyChargeModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<MarkPropertyChargeRequest>(request)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );

      const charge = <PropertyChargeModel>response.body?.valueOf();
      return <ServiceResultModel>{
        data: charge,
        success: true,
        message: 'Invoice marked successfully.',
        statusCode: response.status
      };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async waiveLateFees(
    chargeId: number,
    request: WaiveLateFeesRequest
  ): Promise<ServiceResultModel<PropertyChargeModel>> {
    try {
      const requestUrl = `${this.chargesApiPath}/${chargeId}/waive-late-fees`;
      const response = await firstValueFrom(
        this.httpClient.patch<PropertyChargeModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<WaiveLateFeesRequest>(request)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );

      const charge = <PropertyChargeModel>response.body?.valueOf();
      return <ServiceResultModel>{
        data: charge,
        success: true,
        message: BalancesMessagingConstants.WAIVE_LATE_FEES_SUCCESS().replace('{invoiceId}', String(chargeId)),
        statusCode: response.status
      };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async bulkWaiveLateFees(
    request: WaiveLateFeesBulkActionRequest
  ): Promise<ServiceResultModel<BulkActionResponse>> {
    try {
      const requestUrl = `${this.chargesApiPath}/bulk/waive-late-fees`;
      const response = await firstValueFrom(
        this.httpClient.post<BulkActionResponse>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<WaiveLateFeesBulkActionRequest>(request)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );

      const result = <BulkActionResponse>response.body?.valueOf();
      return <ServiceResultModel>{
        data: result,
        success: true,
        message: BalancesMessagingConstants.WAIVE_LATE_FEES_BULK_SUCCESS()
          .replace('{totalAffected}', String(result?.totalAffected ?? 0))
          .replace('{totalEvaluated}', String(result?.totalEvaluated ?? 0))
          .replace('{totalSkipped}', String(result?.totalSkipped ?? 0)),
        statusCode: response.status
      };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async bulkResendInvoices(
    request: BulkActionRequest<PropertyChargeFilter>
  ): Promise<ServiceResultModel<BulkActionResponse>> {
    try {
      const requestUrl = `${this.chargesApiPath}/bulk/invoice/resend`;
      const response = await firstValueFrom(
        this.httpClient.post<BulkActionResponse>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<BulkActionRequest<PropertyChargeFilter>>(request)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );

      const result = <BulkActionResponse>response.body?.valueOf();
      return <ServiceResultModel>{
        data: result,
        success: true,
        message: BalancesMessagingConstants.BULK_RESEND_INVOICES_SUCCESS()
          .replace('{totalAffected}', String(result?.totalAffected ?? 0))
          .replace('{totalEvaluated}', String(result?.totalEvaluated ?? 0))
          .replace('{totalSkipped}', String(result?.totalSkipped ?? 0)),
        statusCode: response.status
      };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }

  public async unmarkPropertyCharge(
    chargeId: number,
    request: UnmarkPropertyChargeRequest
  ): Promise<ServiceResultModel<PropertyChargeModel>> {
    try {
      const requestUrl = `${this.chargesApiPath}/${chargeId}/unmark`;
      const response = await firstValueFrom(
        this.httpClient.patch<PropertyChargeModel>(
          requestUrl,
          JSON.stringify(APISanitizerUtilities.sanitizePayload<UnmarkPropertyChargeRequest>(request)),
          { observe: "response", headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' } }
        )
      );

      const charge = <PropertyChargeModel>response.body?.valueOf();
      return <ServiceResultModel>{
        data: charge,
        success: true,
        message: BalancesMessagingConstants.UNMARK_PAYMENT_SUCCESS().replace('{invoiceId}', String(chargeId)),
        statusCode: response.status
      };
    } catch (error) {
      return ServiceUtilities.handleServiceException(error);
    }
  }
}
